/**
 * Component: Product form
 * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form.
 * bump
 *
 * @namespace productForm
 */
import { formatMoney } from '@shopify/theme-currency';

import cssClasses from '../helpers/cssClasses';
import { on } from '../helpers/utils';
import bind from '../mixins/bind';

import ProductSwatches from '../components/product-swatches';
// import QuantitySelector from '~/components/quantity-selector';

/**
 * DOM selectors.
 */
const selectors = {
  addToCart: '[js-product-form="addToCart"]',
  comparePrice: '[js-product-form="comparePrice"]',
  container: '[js-product-form="container"]',
  masterSelector: '[js-product-form="masterSelector"]',
  maxQuantityInput: '[js-product-form="maxQuantityInput"]',
  optionSelector: '[js-product-form="optionSelector"]',
  price: '[js-product-form="price"]',
  priceWrapper: '[js-product-form="priceWrapper"]',
  productJson: '[js-product-json]',
  quantitySelector: '[js-quantity-selector="container"]',
};

/**
 * Create a new product form object.
 * @param {Object} config - Configuration.
 */
export default (config) => {
  /**
   * Instance settings.
   */
  const settings = {
    enableHistoryState: config.enableHistoryState || false,
  };

  /**
   * DOM node selectors.
   * // bump
   */
  const container = document.querySelector(
    `[js-product-form="${config.container}"]`
  );

  const nodeSelectors = {
    addToCart: container.querySelector(selectors.addToCart),
    comparePrice: container.querySelector(selectors.comparePrice),
    masterSelector: container.querySelector(selectors.masterSelector),
    optionSelector: container.querySelectorAll(selectors.optionSelector),
    price: container.querySelector(selectors.price),
    priceWrapper: container.querySelector(selectors.priceWrapper),
    quantitySelector: container.querySelector(selectors.quantitySelector),
    maxQuantityInput: container.querySelector(selectors.maxQuantityInput),
    productJson: container.querySelector(selectors.productJson),
  };

  /**
   * Initialise component bind.
   */
  const binder = bind(container);

  /**
   * Global instances.
   */

  const productData = Object.freeze(
    JSON.parse(nodeSelectors.productJson.innerHTML)
  );
  const variantData = productData.variants;

  let quantitySelector = {};
  let currentVariant = {};

  /**
   * Initialise component.
   */
  function init() {
    if (binder.isSet()) {
      return;
    }

    if (!nodeSelectors.addToCart) {
      return;
    }

    if (currentVariant) {
      currentVariant = getVariantFromOptions();
    }

    setDefaultState();
    setEventListeners();
    setEventBusListeners();
    binder.set();
  }

  /**
   * Set default state.
   */
  function setDefaultState() {
    if (theme.features.productSwatches) {
      ProductSwatches({
        container: `[js-product-form="${config.container}"]`,
        data: productData,
      }).init();
    }

    // setQuantitySelectors();
    updateFormState();
  }

  /**
   * Get variant inventory quantity from product-json.liquid variant data.
   * @param {Object || undefined} - Variant object.
   */
  function getVariantInventoryQuantity(variant) {
    if (!variantData.length) {
      return 0;
    }

    return Object.keys(variantData)
      .map((key) => {
        if (variantData[key].id === variant.id) {
          return variantData[key].inventory_quantity;
        }

        return null;
      })
      .filter(Boolean)[0];
  }

  /**
   * Set EventBus listeners.
   */
  function setEventBusListeners() {
    Karmoon.EventBus.listen('Variant:priceChange', (response) =>
      onPriceChange(response)
    );
    Karmoon.EventBus.listen('ProductSwatches:available', (response) =>
      handleSwatchAvailability(response)
    );
    Karmoon.EventBus.listen('ProductSwatches:optionSelected', (response) =>
      handleOptionChange(response)
    );
    Karmoon.EventBus.listen('ProductSwatches:reset', () => resetForm());
    Karmoon.EventBus.listen('ProductSwatches:soldOut', () =>
      updateAddToCartState(theme.strings.soldOut)
    );
  }

  /**
   * Set event listeners on select elements.
   */
  function setEventListeners() {
    [...nodeSelectors.optionSelector].forEach((element) => {
      if (element.nodeName === 'SELECT') {
        on('change', element, () => handleSelectChange());
      }
    });
  }

  /**
   * Get currently selected options from product form.
   * - Works with all form elements with an option selector selector.
   * @returns {Array}
   */
  function getCurrentOptions() {
    const currentOptions = [];

    [...nodeSelectors.optionSelector].forEach((element) => {
      const type = element.getAttribute('type');
      const currentOption = {};

      if (type === 'radio' || type === 'checkbox') {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;

          currentOptions.push(currentOption);
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;

        currentOptions.push(currentOption);
      }
    });

    return currentOptions;
  }

  /**
   * Find variant from product.variants based on selected values.
   * @returns {Object|undefined}
   */
  function getVariantFromOptions() {
    const selectedValues = getCurrentOptions();
    const variants = productData.variants;
    let found = false;

    variants.forEach((variant) => {
      let satisfied = true;

      selectedValues.forEach((option) => {
        if (satisfied) {
          satisfied = option.value === variant[option.index];
        }
      });

      if (satisfied) {
        found = variant;
      }
    });

    return found || null;
  }

  /**
   * Event handler for when a variant price changes.
   * @param {Object} response - Selected variant data.
   */
  function onPriceChange(response) {
    const form = document.querySelector(
      `[js-product-form="${config.container}"]`
    );
    const priceSelector = form.querySelector(selectors.price);

    if (!priceSelector) {
      return;
    }

    priceSelector.setAttribute('data-price', response.price);
    priceSelector.innerText = formatMoney(response.price, theme.moneyFormat);

    if (form.querySelector(selectors.comparePrice)) {
      const comparePriceSelector = form.querySelector(selectors.comparePrice);
      comparePriceSelector.setAttribute(
        'data-price',
        response.compare_at_price
      );

      if (response.compare_at_price > 0) {
        comparePriceSelector.innerText = formatMoney(
          response.compare_at_price,
          theme.moneyFormat
        );
        comparePriceSelector.classList.remove(cssClasses.hidden);
      } else {
        comparePriceSelector.innerText = '';
        comparePriceSelector.classList.add(cssClasses.hidden);
      }
    }

    if (theme.features.currencyConverter) {
      Karmoon.EventBus.emit('Currency:convertAll');
    }
  }

  /**
   * Handle default availability.
   */
  function handleSwatchAvailability(response) {
    if (!response.formCompleted) {
      disableAddToCart(theme.strings.prompt);
    }
  }

  /**
   * Event handler for when an input changes on the form.
   * @param {Object} response - Pay load from option selected.
   */
  function handleOptionChange(response) {
    Karmoon.EventBus.emit('ProductForm:change', {
      currentOptions: getCurrentOptions(),
      currentVariant: response.formCompleted ? getVariantFromOptions() : null,
      currentInventory: getVariantInventoryQuantity(getVariantFromOptions()),
    });

    if (!response.formCompleted) {
      return;
    }

    updateFormState();
  }

  /**
   * Event handler for when a select element changes on the store.
   */
  function handleSelectChange() {
    updateFormState();
  }

  /**
   * Update form state based on current selections.
   */
  function updateFormState() {
    const variant = getVariantFromOptions();

    updateAddToCartState(variant);
    // updateMaxQuantityState(variant);

    if (!variant) {
      return;
    }

    updateMasterSelect(variant);
    updatePrice(variant);

    currentVariant = variant;

    if (settings.enableHistoryState) {
      updateHistoryState(variant);
    }
  }

  /**
   * Updates the DOM state of the add to cart button.
   * @param {{Object || undefined}} variant - Currently selected variant.
   */
  function updateAddToCartState(variant) {
    if (variant) {
      enableAddToCart();

      if (nodeSelectors.priceWrapper) {
        nodeSelectors.priceWrapper.classList.remove(cssClasses.hidden);
      }
    } else {
      disableAddToCart();
      notifyOutOfStock();
      nodeSelectors.addToCart.innerText = theme.strings.unavailable;

      if (nodeSelectors.priceWrapper) {
        nodeSelectors.priceWrapper.classList.add(cssClasses.hidden);
      }
      return;
    }

    if (variant.available) {
      enableAddToCart();
      const preOrder = nodeSelectors.addToCart.dataset.preOrder;

      if (preOrder == 'true') {
        nodeSelectors.addToCart.innerText = `${theme.strings.preOrder}`;
      } else {
        nodeSelectors.addToCart.innerText = `${theme.strings.addToCart}`;
      }
    } else {
      disableAddToCart();
      nodeSelectors.addToCart.innerText = theme.strings.soldOut;
    }
  }

  /**
   * Update max quantity value of quantity selectors.
   * @param {Object} variant - Currently selected variant.
   */
  function updateMaxQuantityState(variant) {
    const quantity =
      getVariantInventoryQuantity(variant) > 0
        ? getVariantInventoryQuantity(variant)
        : 999;

    nodeSelectors.maxQuantityInput.value = quantity;
    quantitySelector.updateAvailability(variant.available);
    quantitySelector.updateMax(quantity);
  }

  /**
   * Trigger event when variant price changes.
   * @param {Object} variant - Currently selected variant.
   */
  function updatePrice(variant) {
    Karmoon.EventBus.emit('Variant:priceChange', variant);
  }

  /**
   * Update hidden master select of variant change.
   * @param {Object} variant - Currently selected variant.
   */
  function updateMasterSelect(variant) {
    nodeSelectors.masterSelector.value = variant.id;
  }

  /**
   * Update history state for product deep linking.
   * @param {Object} variant - Currently selected variant.
   */
  function updateHistoryState(variant) {
    if (!window.history.replaceState || !variant) {
      return;
    }

    const newurl = `
      ${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}
    `;
    window.history.replaceState({ path: newurl }, '', newurl);
  }

  /**
   * Reset form and assign prompt text to add to cart button.
   */
  function resetForm() {
    disableAddToCart();

    if (productData.available) {
      nodeSelectors.addToCart.innerText = theme.strings.prompt;
    }
  }

  /**
   * Set Quantity Selector component.
   */
  function setQuantitySelectors() {
    quantitySelector = QuantitySelector(nodeSelectors.quantitySelector.id);
    quantitySelector.init();
  }

  /**
   * Add disable state to add to cart button.
   * @param {String} label - Text to use on button.
   */
  function disableAddToCart(label = theme.strings.unavailable) {
    nodeSelectors.addToCart.setAttribute('disabled', true);
    nodeSelectors.addToCart.innerText = label;
  }

  /**
   * Hide add to cart button
   */
  function notifyOutOfStock() {
    nodeSelectors.addToCart.style.display = 'none';

    const klavTrigger = document.getElementsByClassName('klaviyo-bis-trigger');
    for (var i = 0; i < klavTrigger.length; i++) {
      klavTrigger.item(i).classList.add('showForNotification');
    }
  }

  /**
   * Set add to cart button to active.
   */
  function enableAddToCart() {
    nodeSelectors.addToCart.removeAttribute('disabled');
    nodeSelectors.addToCart.innerText = theme.strings.addToCart;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    getVariantFromOptions,
  });
};
